import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import MultiSelect from "@/components/MultiSelect";
import { toast } from "sonner";
import { CheckCircle2, Database, MapPin, ArrowRight, ArrowLeft } from "lucide-react";

const stepLabels = ["Data type", "Coverage", "Review"];

export default function CustomerOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState(null);
  const [dataType, setDataType] = useState("both");
  const [gstType, setGstType] = useState("pan_india");
  const [gstStates, setGstStates] = useState([]);
  const [gstCities, setGstCities] = useState([]);
  const [mcaType, setMcaType] = useState("pan_india");
  const [mcaStates, setMcaStates] = useState([]);
  const [mcaDistricts, setMcaDistricts] = useState([]);
  const [mcaRocs, setMcaRocs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/filters/options");
        setOptions(data);
        const my = await api.get("/subscription/my").then((r) => r.data);
        if (my) {
          setExisting(my);
          if (my.status === "pending") {
            nav("/pending");
            return;
          }
          if (my.status === "approved") {
            // Only bounce back to dashboard if the subscription is still ACTIVE.
            // An expired "approved" subscription must let the user request a new one.
            const exp = my.expires_at ? new Date(my.expires_at).getTime() : 0;
            if (exp > Date.now()) {
              nav("/dashboard");
              return;
            }
          }
        }
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const gstCityOpts = useMemo(() => {
    if (!options) return [];
    const set = new Set();
    gstStates.forEach((s) => (options.gst_state_cities[s] || []).forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, [options, gstStates]);

  const mcaDistrictOpts = useMemo(() => {
    if (!options) return [];
    const set = new Set();
    mcaStates.forEach((s) => (options.mca_state_districts[s] || []).forEach((d) => set.add(d)));
    return Array.from(set).sort();
  }, [options, mcaStates]);

  const canNextFromCoverage = () => {
    if (dataType !== "mca") {
      if (gstType === "state" && gstStates.length === 0) return false;
      if (gstType === "city" && (gstStates.length === 0 || gstCities.length === 0)) return false;
    }
    if (dataType !== "gst") {
      if (mcaType === "state" && mcaStates.length === 0) return false;
      if (mcaType === "district" && (mcaStates.length === 0 || mcaDistricts.length === 0)) return false;
      if (mcaType === "roc" && mcaRocs.length === 0) return false;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { data_type: dataType };
      if (dataType !== "mca") {
        payload.gst_coverage = { type: gstType, states: gstStates, cities: gstCities };
      }
      if (dataType !== "gst") {
        payload.mca_coverage = { type: mcaType, states: mcaStates, districts: mcaDistricts, rocs: mcaRocs };
      }
      await api.post("/subscription/request", payload);
      toast.success("Request submitted. Awaiting admin approval.");
      nav("/pending");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!options) return <div className="text-muted-foreground text-sm">Loading options…</div>;

  return (
    <div className="max-w-4xl mx-auto animate-fade-up">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Choose your data package</h1>
        <p className="mt-2 text-sm text-muted-foreground">Configure exactly what you need — an admin will review and approve.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="onboarding-stepper">
        {stepLabels.map((lbl, i) => (
          <React.Fragment key={lbl}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${i === step ? "border-primary bg-primary/10 text-primary" : i < step ? "border-primary/50 text-primary" : "border-border text-muted-foreground"}`}>
              <div className={`h-5 w-5 rounded-full grid place-items-center text-[10px] num ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{i + 1}</div>
              {lbl}
            </div>
            {i < stepLabels.length - 1 && <div className="h-px flex-1 bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-4 w-4 text-primary" />
            <h2 className="font-heading font-semibold text-xl">Which datasets do you need?</h2>
          </div>
          <RadioGroup value={dataType} onValueChange={setDataType} className="grid sm:grid-cols-3 gap-3">
            {[
              { v: "gst", t: "GST only", d: "Newly registered GSTIN businesses" },
              { v: "mca", t: "MCA only", d: "Newly incorporated companies" },
              { v: "both", t: "Both (GST + MCA)", d: "Complete registry feed" },
            ].map((o) => (
              <label key={o.v} htmlFor={`dt-${o.v}`} className={`cursor-pointer rounded-lg border p-4 transition-colors ${dataType === o.v ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} data-testid={`datatype-${o.v}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading font-semibold">{o.t}</div>
                    <div className="text-xs text-muted-foreground mt-1">{o.d}</div>
                  </div>
                  <RadioGroupItem value={o.v} id={`dt-${o.v}`} />
                </div>
              </label>
            ))}
          </RadioGroup>
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setStep(1)} data-testid="onboarding-next-1">Next <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </div>
        </Card>
      )}

      {step === 1 && (
        <div className="space-y-4">
          {dataType !== "mca" && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-primary" />
                <h2 className="font-heading font-semibold text-xl">GST coverage</h2>
              </div>
              <RadioGroup value={gstType} onValueChange={setGstType} className="grid sm:grid-cols-3 gap-3 mb-4">
                {[
                  { v: "pan_india", t: "Pan India", d: "All 36 states/UTs" },
                  { v: "state", t: "State wise", d: "Pick multiple states" },
                  { v: "city", t: "City wise", d: "Pick states → cities" },
                ].map((o) => (
                  <label key={o.v} className={`cursor-pointer rounded-lg border p-3 ${gstType === o.v ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`gst-cov-${o.v}`}>
                    <div className="flex items-start justify-between">
                      <div><div className="font-medium text-sm">{o.t}</div><div className="text-xs text-muted-foreground mt-0.5">{o.d}</div></div>
                      <RadioGroupItem value={o.v} />
                    </div>
                  </label>
                ))}
              </RadioGroup>
              {gstType !== "pan_india" && (
                <div className="space-y-3">
                  <div>
                    <Label>States</Label>
                    <MultiSelect options={options.gst_states} values={gstStates} onChange={setGstStates} placeholder="Select states..." testid="gst-states-select" />
                  </div>
                  {gstType === "city" && (
                    <div>
                      <Label>Cities <span className="text-muted-foreground">({gstCityOpts.length} available)</span></Label>
                      <MultiSelect options={gstCityOpts} values={gstCities} onChange={setGstCities} placeholder="First select states above" disabled={gstStates.length === 0} testid="gst-cities-select" />
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {dataType !== "gst" && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-primary" />
                <h2 className="font-heading font-semibold text-xl">MCA coverage</h2>
              </div>
              <RadioGroup value={mcaType} onValueChange={setMcaType} className="grid sm:grid-cols-4 gap-3 mb-4">
                {[
                  { v: "pan_india", t: "Pan India" },
                  { v: "state", t: "State wise" },
                  { v: "district", t: "District wise" },
                  { v: "roc", t: "ROC wise" },
                ].map((o) => (
                  <label key={o.v} className={`cursor-pointer rounded-lg border p-3 ${mcaType === o.v ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`mca-cov-${o.v}`}>
                    <div className="flex items-start justify-between">
                      <div className="font-medium text-sm">{o.t}</div>
                      <RadioGroupItem value={o.v} />
                    </div>
                  </label>
                ))}
              </RadioGroup>
              {(mcaType === "state" || mcaType === "district") && (
                <div className="space-y-3">
                  <div>
                    <Label>States</Label>
                    <MultiSelect options={options.mca_states} values={mcaStates} onChange={setMcaStates} placeholder="Select states..." testid="mca-states-select" />
                  </div>
                  {mcaType === "district" && (
                    <div>
                      <Label>Districts <span className="text-muted-foreground">({mcaDistrictOpts.length} available)</span></Label>
                      <MultiSelect options={mcaDistrictOpts} values={mcaDistricts} onChange={setMcaDistricts} placeholder="First select states above" disabled={mcaStates.length === 0} testid="mca-districts-select" />
                    </div>
                  )}
                </div>
              )}
              {mcaType === "roc" && (
                <div>
                  <Label>ROCs</Label>
                  <MultiSelect options={options.rocs} values={mcaRocs} onChange={setMcaRocs} placeholder="Select ROCs..." testid="mca-rocs-select" />
                </div>
              )}
            </Card>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)} data-testid="onboarding-back-1"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
            <Button onClick={() => setStep(2)} disabled={!canNextFromCoverage()} data-testid="onboarding-next-2">Review <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <Card className="p-6">
          <h2 className="font-heading font-semibold text-xl mb-4">Review & submit</h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
              <div>
                <div className="font-medium">Data type: <span className="text-primary">{dataType.toUpperCase()}</span></div>
              </div>
            </div>
            {dataType !== "mca" && (
              <div className="rounded-md border border-border p-3 bg-muted/30">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">GST coverage</div>
                <div className="mt-1 font-medium">{gstType.replace("_", " ")}</div>
                {gstStates.length > 0 && <div className="mt-1 text-xs">States: {gstStates.join(", ")}</div>}
                {gstCities.length > 0 && <div className="mt-1 text-xs">Cities: {gstCities.join(", ")}</div>}
              </div>
            )}
            {dataType !== "gst" && (
              <div className="rounded-md border border-border p-3 bg-muted/30">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">MCA coverage</div>
                <div className="mt-1 font-medium">{mcaType.replace("_", " ")}</div>
                {mcaStates.length > 0 && <div className="mt-1 text-xs">States: {mcaStates.join(", ")}</div>}
                {mcaDistricts.length > 0 && <div className="mt-1 text-xs">Districts: {mcaDistricts.join(", ")}</div>}
                {mcaRocs.length > 0 && <div className="mt-1 text-xs">ROCs: {mcaRocs.join(", ")}</div>}
              </div>
            )}
            <div className="text-xs text-muted-foreground">Once submitted, your request will go to an admin. You'll get a notification when it's approved.</div>
          </div>
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="onboarding-back-2"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
            <Button onClick={submit} disabled={submitting} className="btn-shine" data-testid="onboarding-submit">
              {submitting ? "Submitting..." : "Submit for approval"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
